import sodium from 'libsodium-wrappers';
import { scorePassword } from './passwordStrength';

const MIN_PASSWORD_LENGTH = 8;

const appEl = document.getElementById('app') as HTMLElement;

interface InvitationPublicInfo {
  status: 'pending' | 'completed' | 'expired' | 'stale';
  senderEmail?: string;
  senderPublicKey?: string;
  expiresAt?: string;
}

function getTokenFromPath(): string | null {
  const match = window.location.pathname.match(/\/invite\/([^/]+)/);
  return match ? decodeURIComponent(match[1] as string) : null;
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

function renderError(message: string): void {
  appEl.innerHTML = '';
  const panel = document.createElement('div');
  panel.className = 'state-panel error';
  const p = document.createElement('p');
  p.textContent = message;
  panel.innerHTML = '<div class="icon">&#9888;</div>';
  panel.appendChild(p);
  appEl.appendChild(panel);
}

function renderSuccess(): void {
  appEl.innerHTML = '';
  const panel = document.createElement('div');
  panel.className = 'state-panel success';
  panel.innerHTML = '<div class="icon">&#10003;</div><h1>Passwort gespeichert</h1>';
  const p = document.createElement('p');
  p.textContent =
    'Ihr Passwort wurde sicher übermittelt. Sie können dieses Fenster jetzt schließen.';
  panel.appendChild(p);
  appEl.appendChild(panel);
}

function mapErrorMessage(status: number, errorCode?: string): string {
  switch (errorCode) {
    case 'already_used':
      return 'Für diese Einladung wurde bereits ein Passwort festgelegt.';
    case 'expired':
      return 'Diese Einladung ist abgelaufen.';
    case 'stale_key':
      return 'Der Absender hat seinen Schlüssel seit dieser Einladung erneuert. Bitten Sie um eine neue Einladung.';
    case 'not_found':
      return 'Diese Einladung wurde nicht gefunden.';
    default:
      return status === 429
        ? 'Zu viele Versuche. Bitte warten Sie einen Moment und versuchen Sie es erneut.'
        : 'Das Passwort konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.';
  }
}

function renderForm(token: string, senderEmail: string, senderPublicKeyBase64: string): void {
  appEl.innerHTML = `
    <h1>Verschlüsselungspasswort festlegen</h1>
    <p class="subtitle"><strong>${escapeHtml(senderEmail)}</strong> hat Sie eingeladen, ein Passwort
    für die verschlüsselte E-Mail-Kommunikation festzulegen. Das Passwort wird direkt in Ihrem
    Browser verschlüsselt und ist für niemanden außer ${escapeHtml(senderEmail)} lesbar.</p>
    <form id="password-form" novalidate>
      <label for="password">Passwort</label>
      <input type="password" id="password" name="password" autocomplete="new-password" required />
      <div class="strength-track"><div class="strength-fill" id="strength-fill"></div></div>
      <div class="strength-label" id="strength-label"></div>

      <label for="confirm">Passwort bestätigen</label>
      <input type="password" id="confirm" name="confirm" autocomplete="new-password" required />
      <div class="match-indicator" id="match-indicator"></div>

      <div class="form-error" id="form-error"></div>
      <button type="submit" id="submit-btn" disabled>Passwort festlegen</button>
    </form>
  `;

  const form = document.getElementById('password-form') as HTMLFormElement;
  const passwordInput = document.getElementById('password') as HTMLInputElement;
  const confirmInput = document.getElementById('confirm') as HTMLInputElement;
  const strengthFill = document.getElementById('strength-fill') as HTMLDivElement;
  const strengthLabel = document.getElementById('strength-label') as HTMLDivElement;
  const matchIndicator = document.getElementById('match-indicator') as HTMLDivElement;
  const formError = document.getElementById('form-error') as HTMLDivElement;
  const submitBtn = document.getElementById('submit-btn') as HTMLButtonElement;

  function updateUi(): void {
    const password = passwordInput.value;
    const confirm = confirmInput.value;

    const strength = scorePassword(password);
    strengthFill.style.width = `${(strength.score / 4) * 100}%`;
    strengthFill.style.backgroundColor = `var(${strength.colorVar})`;
    strengthLabel.textContent = password ? strength.label : '';

    if (!confirm) {
      matchIndicator.textContent = '';
      matchIndicator.className = 'match-indicator';
    } else if (password === confirm) {
      matchIndicator.textContent = 'Passwörter stimmen überein';
      matchIndicator.className = 'match-indicator match';
    } else {
      matchIndicator.textContent = 'Passwörter stimmen nicht überein';
      matchIndicator.className = 'match-indicator mismatch';
    }

    submitBtn.disabled = !(password.length >= MIN_PASSWORD_LENGTH && password === confirm);
  }

  passwordInput.addEventListener('input', updateUi);
  confirmInput.addEventListener('input', updateUi);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void submit();
  });

  async function submit(): Promise<void> {
    formError.textContent = '';
    submitBtn.disabled = true;
    submitBtn.textContent = 'Wird gesendet…';

    try {
      await sodium.ready;
      const passwordBytes = sodium.from_string(passwordInput.value);
      const publicKeyBytes = sodium.from_base64(
        senderPublicKeyBase64,
        sodium.base64_variants.ORIGINAL,
      );
      const sealed = sodium.crypto_box_seal(passwordBytes, publicKeyBytes);
      const ciphertext = sodium.to_base64(sealed, sodium.base64_variants.ORIGINAL);

      // The plaintext password never leaves this function scope beyond this point, and is
      // never written to localStorage/sessionStorage or logged anywhere.
      passwordInput.value = '';
      confirmInput.value = '';

      const res = await fetch(`/api/invitations/${encodeURIComponent(token)}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ciphertext }),
      });

      if (res.ok) {
        renderSuccess();
        return;
      }

      const body = (await res.json().catch(() => ({}))) as { error?: string };
      formError.textContent = mapErrorMessage(res.status, body.error);
      submitBtn.disabled = false;
      submitBtn.textContent = 'Passwort festlegen';
    } catch {
      formError.textContent =
        'Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es erneut.';
      submitBtn.disabled = false;
      submitBtn.textContent = 'Passwort festlegen';
    }
  }

  updateUi();
}

async function main(): Promise<void> {
  const token = getTokenFromPath();
  if (!token) {
    renderError('Ungültiger Link.');
    return;
  }

  let res: Response;
  try {
    res = await fetch(`/api/invitations/token/${encodeURIComponent(token)}`);
  } catch {
    renderError(
      'Die Einladung konnte nicht geladen werden. Bitte versuchen Sie es später erneut.',
    );
    return;
  }

  if (!res.ok) {
    renderError('Diese Einladung wurde nicht gefunden oder ist ungültig.');
    return;
  }

  const info = (await res.json()) as InvitationPublicInfo;

  switch (info.status) {
    case 'pending':
      renderForm(token, info.senderEmail as string, info.senderPublicKey as string);
      break;
    case 'completed':
      renderError('Für diese Einladung wurde bereits ein Passwort festgelegt.');
      break;
    case 'expired':
      renderError(
        'Diese Einladung ist abgelaufen. Bitten Sie den Absender, eine neue Einladung zu senden.',
      );
      break;
    case 'stale':
      renderError(
        'Der Absender hat seinen Schlüssel seit dieser Einladung erneuert. Bitten Sie um eine neue Einladung.',
      );
      break;
    default:
      renderError('Diese Einladung ist nicht mehr gültig.');
  }
}

void main();
